/**
 * Table Controller for Poker Reborn
 *
 * Manages table actions: watching, sitting, leaving, game actions.
 * Connects to poker_platform backend via croupier services.
 */

import { networkService } from '@/services/NetworkService';
import { accountService } from '@/services/AccountService';
import { EventEmitter } from '@/services/EventEmitter';
import { log } from '@/utils/log';
import { decodeProfile } from '@/utils/profile';
import { gameEventBus } from '@/effects/events';
import {
  tableState,
  joinTable,
  leaveTable,
  sitDown,
  standUp,
  setPlayers,
  updatePlayer,
  setDealer,
  setActivePlayer,
  setHeroSitout,
  setSitoutTimer,
  clearSitoutTimer,
  tickSitoutTimers,
  setCommunityCards,
  setStreet,
  setPots,
  setHeroCards,
  markBrUpdated,
} from '@/state/TableState';
import { appState, navigateToScreen } from '@/state/AppState';
import { Commands } from '@/types/protocol';
import { AvatarService, avatarService } from '@/services/AvatarService';
import { giftService } from '@/services/GiftService';
import { getAvatarPath } from '@/config/avatars';
import type { TableInfo, TableSize, Stakes, PlayerState, PlayerProfile, Card, PotState } from '@/types/game';

// =============================================================================
// TYPES
// =============================================================================

export interface TableControllerEvents {
  /** Joined table (watch mode) */
  joined: { tableId: string };

  /** Left table */
  left: void;

  /** Sat down at seat */
  seated: { seat: number; buyIn: number };

  /** Stood up from seat */
  stoodUp: void;

  /** Sat out from game (still at table) */
  satOut: void;

  /** Came back from sitout */
  cameBack: void;

  /** Error occurred */
  error: { message: string };

  /** Buy-in error (seat/buyin rejected by server) */
  buyinError: { message: string; code?: number };

  /** Buy-in requested */
  buyInRequested: { seat: number; minBuyIn: number; maxBuyIn: number };

  /** Rebuy requested (player lost all chips) */
  rebuyRequested: {
    minBuyIn: number;
    maxBuyIn: number;
    timeout: number;
    at: 'end' | 'start';
  };
}

// =============================================================================
// TABLE CONTROLLER
// =============================================================================

class TableController extends EventEmitter<TableControllerEvents> {
  private static instance: TableController | null = null;
  private pendingSeat: number | null = null;
  private currentCroupier: string | null = null;
  private sitoutTickInterval: number | null = null;

  private constructor() {
    super();
    this.setupNetworkHandlers();
  }

  /** Get singleton instance */
  static getInstance(): TableController {
    if (!TableController.instance) {
      TableController.instance = new TableController();
    }
    return TableController.instance;
  }

  /**
   * Recover session at a table after disconnect (browser refresh, etc.)
   * Called when AccountService detects an active lock.
   *
   * Per POKERTALK.md Session Recovery:
   * 1. Subscribe to croupier
   * 2. Send # jumpin (any seat/buyin - will trigger double_entry)
   * 3. Server detects player already has seat → sends state + act messages
   *
   * @param croupierId - The croupier GLID from the lock
   */
  async recoverTable(croupierId: string): Promise<void> {
    log.table.info`Recovering session at table: ${croupierId}`;

    // Store croupier ID
    this.currentCroupier = croupierId;

    // Set up minimal table state (will be updated by server state response)
    // We use placeholder values since server will send full state
    joinTable(croupierId, 'Recovering...', 6, { smallBlind: 1, bigBlind: 2 });

    // Navigate to table screen immediately
    navigateToScreen('table');

    // Send jumpin to trigger double_entry recovery
    // Per POKERTALK.md: seat number and buyin are ignored for double_entry
    if (networkService.isConnected()) {
      const command = Commands.jumpInRecover();
      log.table.info`Sending recovery jumpin to ${croupierId}`;
      networkService.send(croupierId, command);

      // Also request balance for RoomBar display
      accountService.requestBalance();
    }

    this.emit('joined', { tableId: croupierId });
  }

  /**
   * Watch a table by croupier GLID only (spectate mode via URL param)
   * Table info will be populated from server's state response.
   */
  async watchTableById(croupierGlid: string): Promise<void> {
    log.table.info`Watching table by ID: ${croupierGlid}`;

    // Store croupier glid
    this.currentCroupier = croupierGlid;

    // Set up minimal table state (will be updated by server state response)
    // Use placeholder values since server will send full state
    joinTable(croupierGlid, 'Loading...', 6, { smallBlind: 1, bigBlind: 2 });

    // Navigate to table screen
    navigateToScreen('table');

    // Send watch request - server will respond with full state
    if (networkService.isConnected()) {
      const command = Commands.watch();
      log.table.debug`Sending # watch to ${croupierGlid}`;
      networkService.send(croupierGlid, command);

      // Request balance from ACC for RoomBar display
      accountService.requestBalance();
    }

    this.emit('joined', { tableId: croupierGlid });
  }

  /**
   * Watch a table (spectate mode)
   * @param tableInfo - Table information
   * @param options - Optional settings for joining behavior
   * @param options.buyInAmount - Pre-selected buy-in amount (shows seat selection, then auto-buys)
   * @param options.spectatorMode - If true, join as spectator only (no auto-seat)
   * @param options.autoRefill - Auto refill when chips run low
   * @param options.autoRebuy - Auto rebuy when busted
   */
  async watchTable(
    tableInfo: TableInfo,
    options?: { buyInAmount?: number; spectatorMode?: boolean; autoRefill?: boolean; autoRebuy?: boolean }
  ): Promise<void> {
    const stakes: Stakes = {
      smallBlind: tableInfo.stakes.smallBlind,
      bigBlind: tableInfo.stakes.bigBlind,
    };

    // Store croupier glid (table id is the croupier glid)
    this.currentCroupier = tableInfo.id;

    // Store options for the table screen to use
    this.pendingBuyInAmount = options?.buyInAmount;
    this.isSpectatorMode = options?.spectatorMode ?? false;
    this.pendingAutoRefill = options?.autoRefill ?? false;
    this.pendingAutoRebuy = options?.autoRebuy ?? false;

    // Join table state
    joinTable(tableInfo.id, tableInfo.name, tableInfo.seats, stakes);

    // Navigate to table screen
    navigateToScreen('table');

    // Send watch request to croupier (if connected)
    // # watch subscribes to table updates and server responds with full state
    if (networkService.isConnected()) {
      const command = Commands.watch();
      log.table.debug`Sending # watch to ${this.currentCroupier}`;
      networkService.send(this.currentCroupier, command);

      // Request balance from ACC for RoomBar display
      log.account.debug`Requesting balance on table enter`;
      accountService.requestBalance();
    }

    this.emit('joined', { tableId: tableInfo.id });
  }

  /** Pending buy-in amount from lobby selection */
  pendingBuyInAmount: number | undefined = undefined;

  /** Whether player joined as spectator (no auto-seat) */
  isSpectatorMode = false;

  /** Pending auto-refill option from lobby selection */
  pendingAutoRefill = false;

  /** Pending auto-rebuy option from lobby selection */
  pendingAutoRebuy = false;

  /**
   * Request to sit at a specific seat
   */
  requestSit(seat: number): void {
    const tableId = tableState.tableId.get();
    if (!tableId) {
      this.emit('error', { message: 'Not at a table' });
      return;
    }

    // Check if seat is empty
    const players = tableState.players.get();
    if (players.has(seat)) {
      this.emit('error', { message: 'Seat is occupied' });
      return;
    }

    // Store pending seat for buy-in
    this.pendingSeat = seat;

    // Get buy-in limits from server game info (min_buyin/max_buyin are BB multipliers)
    const stakes = tableState.stakes.get();
    const gameInfo = tableState.gameInfo.get();
    const minBuyIn = (gameInfo?.minBuyin ?? 20) * stakes.bigBlind;
    const maxBuyIn = (gameInfo?.maxBuyin ?? 200) * stakes.bigBlind;

    // Request buy-in popup
    this.emit('buyInRequested', { seat, minBuyIn, maxBuyIn });
  }

  /**
   * Jump in to any free seat with buy-in amount
   * Sends "# jumpin reserve buyin <amount> <autorefill> <autorebuy>" to croupier
   * Server picks the seat automatically.
   * @param buyIn - Buy-in amount
   */
  jumpIn(buyIn: number): void {
    // Use pending values from lobby
    const refill = this.pendingAutoRefill;
    const rebuy = this.pendingAutoRebuy;

    // Clear pending values after use
    this.pendingBuyInAmount = undefined;
    this.pendingAutoRefill = false;
    this.pendingAutoRebuy = false;

    // Send jumpin request to croupier (if connected)
    if (networkService.isConnected() && this.currentCroupier) {
      const clampedBuyIn = this.clampBuyIn(buyIn);
      const autoRefillFlag = refill ? 1 : 0;
      const autoRebuyFlag = rebuy ? 1 : 0;
      const command = Commands.jumpIn(clampedBuyIn, undefined, autoRefillFlag, autoRebuyFlag);
      log.table.debug`Sending jumpin: buyin ${clampedBuyIn} autoRefill=${refill} autoRebuy=${rebuy}`;
      networkService.send(this.currentCroupier, command);
    }
  }

  /**
   * Confirm sit with buy-in amount (for specific seat)
   * Sends "# jumpin reserve <seat> buyin <amount> <autorefill> <autorebuy>" to croupier
   * @param buyIn - Buy-in amount
   * @param autoRefill - Auto refill when chips run low (optional, uses pending value)
   * @param autoRebuy - Auto rebuy when busted (optional, uses pending value)
   */
  confirmSit(buyIn: number, autoRefill?: boolean, autoRebuy?: boolean): void {
    if (this.pendingSeat === null) {
      this.emit('error', { message: 'No pending seat request' });
      return;
    }

    const seat = this.pendingSeat;
    this.pendingSeat = null;

    // Use provided values or fall back to pending values from lobby
    const refill = autoRefill ?? this.pendingAutoRefill;
    const rebuy = autoRebuy ?? this.pendingAutoRebuy;

    // Clear pending values after use
    this.pendingBuyInAmount = undefined;
    this.pendingAutoRefill = false;
    this.pendingAutoRebuy = false;

    // Send sit request to croupier (if connected)
    if (networkService.isConnected() && this.currentCroupier) {
      // Server uses 1-based seat numbering, client uses 0-based
      const serverSeat = seat + 1;
      const clampedBuyIn = this.clampBuyIn(buyIn);
      const autoRefillFlag = refill ? 1 : 0;
      const autoRebuyFlag = rebuy ? 1 : 0;
      const command = Commands.jumpIn(clampedBuyIn, serverSeat, autoRefillFlag, autoRebuyFlag);
      log.table.debug`Sending jumpin: seat ${serverSeat} buyin ${clampedBuyIn} autoRefill=${refill} autoRebuy=${rebuy}`;
      networkService.send(this.currentCroupier, command);
    } else {
      // For development without server: immediately sit
      this.handleSitSuccess(seat, buyIn);
    }
  }

  /**
   * Cancel sit request
   */
  cancelSit(): void {
    this.pendingSeat = null;
  }

  /**
   * Sit out from game (pause play while staying at table)
   * Sends "# sitout" to croupier (per POKERTALK.md)
   * Player can come back later with requestBack()
   */
  requestSitout(): void {
    const heroSeat = tableState.heroSeat.get();
    if (heroSeat === null) {
      this.emit('error', { message: 'Not seated' });
      return;
    }

    // Send sitout request to croupier (if connected)
    if (networkService.isConnected() && this.currentCroupier) {
      const command = Commands.sitout();
      log.table.debug`Sending sitout`;
      networkService.send(this.currentCroupier, command);
    } else {
      // For development without server: immediately set sitout
      setHeroSitout(true);
      this.emit('satOut', undefined);
    }
  }

  /**
   * Come back from sitout
   * Sends "# back" to croupier (per POKERTALK.md)
   */
  requestBack(): void {
    const heroSeat = tableState.heroSeat.get();
    if (heroSeat === null) {
      this.emit('error', { message: 'Not seated' });
      return;
    }

    // Send back request to croupier (if connected)
    if (networkService.isConnected() && this.currentCroupier) {
      const command = Commands.back();
      log.table.debug`Sending back`;
      networkService.send(this.currentCroupier, command);
    } else {
      // For development without server: immediately set back
      setHeroSitout(false);
      this.emit('cameBack', undefined);
    }
  }

  /**
   * Leave the table completely
   * Sends "# leave" to croupier
   */
  leave(): void {
    const tableId = tableState.tableId.get();
    if (!tableId) return;

    // Send leave request to croupier (if connected)
    if (networkService.isConnected() && this.currentCroupier) {
      const command = '# leave';
      log.table.debug`Sending leave`;
      networkService.send(this.currentCroupier, command);
    }

    // Clear current croupier
    this.currentCroupier = null;

    leaveTable();
    navigateToScreen('lobby');
    this.emit('left', undefined);
  }

  /**
   * Get current table size
   */
  getTableSize(): TableSize {
    return tableState.tableSize.get();
  }

  /**
   * Get all players
   */
  getPlayers(): Map<number, PlayerState> {
    return tableState.players.get();
  }

  /**
   * Get empty seats
   */
  getEmptySeats(): number[] {
    const tableSize = tableState.tableSize.get();
    const players = tableState.players.get();
    const emptySeats: number[] = [];

    for (let i = 0; i < tableSize; i++) {
      if (!players.has(i)) {
        emptySeats.push(i);
      }
    }

    return emptySeats;
  }

  /**
   * Check if seat is empty
   */
  isSeatEmpty(seat: number): boolean {
    return !tableState.players.get().has(seat);
  }

  /**
   * Check if hero is seated
   */
  isSeated(): boolean {
    return tableState.heroSeat.get() !== null;
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private setupNetworkHandlers(): void {
    networkService.on('message', ({ from, body }) => {
      try {
        const message = JSON.parse(body) as Record<string, unknown>;
        this.handleNetworkMessage(from, message);
      } catch {
        // Not a JSON message we handle
      }
    });
  }

  private handleNetworkMessage(from: string, message: Record<string, unknown>): void {
    // Source validation: Only process messages from current croupier
    // Exception: ping messages should always be handled to maintain connection
    if (message.a !== 'ping' && this.currentCroupier !== null && from !== this.currentCroupier) {
      log.table.debug`Ignoring message from ${from} (current croupier: ${this.currentCroupier})`;
      return;
    }

    log.table.debug`Received ${message.a} from ${from}`;

    switch (message.a) {
      // Ping - respond with pong to stay connected (REQUIRED!)
      case 'ping':
        this.handlePing(from);
        break;

      case 'state':
      case 'state2':
        log.table.info`Received state message from server`;
        this.handleStateUpdate(message);
        break;

      // Server confirms seat reservation
      case 'reserved':
        this.handleReserved(message);
        break;

      // Server confirms successful sit-in
      case 'sitin':
      case 'sitin2':
        this.handleSitIn(message);
        break;

      // Player left/stood up
      case 'leave':
      case 'standup':
        this.handlePlayerLeft(message);
        break;

      // Seat became free (player left, simplified message)
      case 'free':
      case 'seat_free':
        this.handleSeatFree(message);
        break;

      // Player sat out (paused play)
      case 'sitout':
        this.handleSitout(message);
        break;

      // Player came back from sitout
      case 'back':
        this.handleBack(message);
        break;

      // Bankroll update (rebuy/refill/addon broadcast to all players)
      case 'br':
        this.handleBankrollUpdate(message);
        break;

      // Rebuy request (player lost all chips)
      case 'rebuy':
        this.handleRebuy(message);
        break;

      // Error responses
      case 'error':
        this.handleServerError(message);
        break;

      // Gift broadcast from croupier (T42)
      case 'gift':
        giftService.handleServerGift(message);
        break;

      // Legacy compatibility
      case 'sitok':
        this.handleSitOk(message);
        break;
      case 'siterror':
        this.handleSitError(message);
        break;
    }
  }

  private handleStateUpdate(message: Record<string, unknown>): void {
    log.table.debug`Processing state update`;

    // Parse table info if present
    const info = message.info as Record<string, unknown> | undefined;
    if (info) {
      // Update table size if provided
      const numSeats = info.num_seats as number | undefined;
      if (numSeats && numSeats !== tableState.tableSize.get()) {
        tableState.tableSize.set(numSeats as TableSize);
        log.table.debug`Table size updated: ${numSeats}`;
      }

      // Update stakes if provided
      const sb = info.sb as number | undefined;
      const bb = info.bb as number | undefined;
      if (sb !== undefined && bb !== undefined) {
        tableState.stakes.set({ smallBlind: sb, bigBlind: bb });
        log.table.debug`Stakes updated: ${sb}/${bb}`;
      }

      // Update game info (min/max buyin from server)
      const minBuyin = info.min_buyin as number | undefined;
      const maxBuyin = info.max_buyin as number | undefined;
      if (minBuyin !== undefined && maxBuyin !== undefined) {
        const currentGameInfo = tableState.gameInfo.get();
        const currentStakes = tableState.stakes.get();
        tableState.gameInfo.set({
          tableSize: (numSeats ?? tableState.tableSize.get()) as TableSize,
          stakes: { smallBlind: sb ?? currentStakes.smallBlind, bigBlind: bb ?? currentStakes.bigBlind },
          minBuyin,
          maxBuyin,
          actionTimeout: Number(info.time_to_act ?? currentGameInfo?.actionTimeout ?? 12),
          gameType: currentGameInfo?.gameType ?? 'holdem',
        });
        log.table.debug`Game info updated: minBuyin=${minBuyin} maxBuyin=${maxBuyin}`;
      }
    }

    // Parse and update player states
    // Note: Server uses 1-based seats, client uses 0-based
    const playersData = message.players as unknown[] | undefined;
    let dealerSeat: number | null = null;

    if (playersData) {
      const playersMap = new Map<number, PlayerState>();
      playersData.forEach((p: unknown) => {
        const playerData = p as Record<string, unknown>;
        const serverSeat = Number(playerData.sn ?? 1);
        const seat = serverSeat - 1; // Convert to 0-based
        const profile = playerData.player as Record<string, unknown> | undefined;

        const avatarUrl = profile?.avatar;
        const playerId = String(profile?.id ?? '');
        const myGlid = networkService.getMyGlid();
        const isHero = playerId === myGlid || playerId === 'you';

        const playerProfile: PlayerState['profile'] = {
          id: playerId,
          name: isHero ? appState.playerDisplayName.get() : String(profile?.name ?? profile?.id ?? 'Player'),
        };
        if (typeof avatarUrl === 'string') {
          playerProfile.avatarUrl = avatarUrl;
        }

        // Decode CRM profile data (JSON)
        const decodedProfile = decodeProfile(profile?.profile);
        if (decodedProfile) {
          playerProfile.crmData = decodedProfile;

          // Extract nickname from profile
          if (typeof decodedProfile.nickname === 'string' && decodedProfile.nickname.length > 0) {
            playerProfile.name = decodedProfile.nickname;
          }

          // Extract avatar from profile
          const profileAvatarId = AvatarService.resolveAvatarId(decodedProfile);
          if (profileAvatarId !== null) {
            playerProfile.avatarUrl = getAvatarPath(profileAvatarId);
          }

          // For hero player, sync avatar from backend profile
          if (isHero) {
            avatarService.updateFromProfile(decodedProfile);
          }
        }

        const stack = Number(playerData.br ?? 0);

        // Determine player status from server state
        let status: PlayerState['status'] = 'active';
        const serverState = playerData.state as string | undefined;
        if (serverState === 'sitout') {
          status = 'sitout';
        } else if (serverState === 'fold' || playerData.la === 'fold') {
          status = 'folded';
        } else if (playerData.la === 'allin') {
          status = 'allin';
        }

        // Parse hole cards if present
        let holeCards: [Card, Card] | null = null;
        const holeData = playerData.hole as string[] | undefined;
        if (holeData && holeData.length === 2) {
          const card1 = this.parseCardCode(holeData[0] as string);
          const card2 = this.parseCardCode(holeData[1] as string);
          if (card1 && card2) {
            holeCards = [card1, card2];
          }
        }

        // Check if player has cards (for showing card backs)
        const hasCards = Boolean(playerData.has_cards);

        const playerState: PlayerState = {
          profile: playerProfile,
          seat,
          stack,
          // pps = current street bet amount (what's displayed next to the player)
          // pp = cumulative pot contribution (total across all streets)
          bet: Number(playerData.pps ?? 0),
          status,
          position: (playerData.pos as PlayerState['position']) ?? '',
          holeCards,
          hasCards,
          isHero,
        };

        // Only set lastAction if it's defined
        const lastAction = playerData.la as PlayerState['lastAction'] | undefined;
        if (lastAction) {
          playerState.lastAction = lastAction;
        }

        // Track dealer position (btn position)
        if (playerData.pos === 'btn') {
          dealerSeat = seat;
        }

        // Track sitout timer for recovery
        const sitoutTimer = playerData.sitout_timer as number | undefined;
        if (status === 'sitout' && sitoutTimer !== undefined && sitoutTimer > 0) {
          setSitoutTimer(seat, sitoutTimer);
          log.table.debug`Sitout timer restored for seat ${seat}: ${sitoutTimer}s`;
        }

        // Update hero seat in table state if this is us
        // Only update heroSeat if we explicitly identify as 'you' (server sends this for the current player)
        // Don't rely on myGlid matching alone - 'you' is the authoritative marker from server
        if (isHero) {
          // 'you' is the server's way of saying "this is you" - trust it for seated players
          // myGlid matching alone isn't enough (could be stale or match accidentally)
          const serverSaysItsMe = playerId === 'you';

          if (serverSaysItsMe) {
            sitDown(seat);
            log.table.debug`Hero state updated (server confirmed): seat ${seat} stack ${stack}`;

            // Set hero's hole cards if present (for session recovery)
            if (holeCards) {
              setHeroCards(holeCards);
              log.table.debug`Hero hole cards restored`;
            }

            // Set hero sitout state if player is sitting out (for session recovery)
            if (status === 'sitout') {
              setHeroSitout(true);
              log.table.debug`Hero sitout state restored`;
            }
          } else {
            log.table.debug`Skipping sitDown (playerId=${playerId} matches myGlid but not 'you')`;
          }
        }

        playersMap.set(seat, playerState);
      });
      setPlayers(playersMap);

      // Start sitout tick if any timers were recovered
      const timers = tableState.sitoutTimers.get();
      if (timers.size > 0) {
        this.startSitoutTick();
      }
    }

    // Update dealer position from player with 'btn' position
    if (dealerSeat !== null) {
      setDealer(dealerSeat);
      log.table.debug`Dealer position: seat ${dealerSeat}`;
    } else if (typeof message.btn === 'number') {
      // Fallback to btn field if present
      setDealer(message.btn - 1); // Convert to 0-based
    }

    // Update active player from current_player field
    const currentPlayer = message.current_player as string | undefined;
    if (currentPlayer) {
      // Find the seat for this player
      const players = tableState.players.get();
      let foundSeat: number | null = null;
      let foundPlayerId = '';
      let foundIsHero = false;

      players.forEach((p, seat) => {
        if (p.profile.id === currentPlayer || (currentPlayer === 'you' && p.isHero)) {
          foundSeat = seat;
          foundPlayerId = p.profile.id;
          foundIsHero = p.isHero;
        }
      });

      if (foundSeat !== null) {
        // Set active player for state sync (turn:started event is emitted by GameController from 'act' message)
        setActivePlayer(foundSeat);
        log.table.debug`Active player: seat ${foundSeat}`;
      }
    } else if (typeof message.active === 'number') {
      // Fallback to active field if present
      // Set active player for state sync (turn:started event is emitted by GameController from 'act' message)
      const activeSeatFromMessage = message.active - 1; // Convert to 0-based
      setActivePlayer(activeSeatFromMessage);
    }

    // Update community cards (board)
    const board = message.board as string[] | undefined;
    console.log('[TableController] handleStateUpdate - board:', board);
    if (board && board.length > 0) {
      const cards = board
        .map((cardCode) => this.parseCardCode(cardCode))
        .filter((c): c is Card => c !== null);
      console.log('[TableController] Setting community cards:', cards.length, 'cards');
      setCommunityCards(cards);
      log.table.debug`Community cards: ${board.join(' ')}`;

      // Determine street from number of community cards
      if (cards.length === 3) {
        setStreet('flop');
      } else if (cards.length === 4) {
        setStreet('turn');
      } else if (cards.length === 5) {
        setStreet('river');
      }
    } else {
      // No board means preflop or waiting
      console.log('[TableController] No board in state message - clearing community cards');
      setCommunityCards([]);
      setStreet('preflop');
    }

    // Update pots
    const potsData = message.pots as Array<{
      id: number;
      pot_gross: number;
      participants: string[];
    }> | undefined;
    if (potsData) {
      const pots: PotState[] = potsData.map((p) => ({
        id: p.id,
        amount: p.pot_gross,
        eligiblePlayerIds: p.participants,
      }));
      setPots(pots);
      log.table.debug`Pots updated: ${pots.length} pot(s)`;
    }

    // Update game number (can be string UUID or number)
    const gameNumber = message.gn;
    if (gameNumber !== undefined) {
      if (typeof gameNumber === 'number') {
        tableState.gameNumber.set(gameNumber);
      } else if (typeof gameNumber === 'string') {
        // Convert UUID string to a numeric hash
        let hash = 0;
        for (let i = 0; i < gameNumber.length; i++) {
          const char = gameNumber.charCodeAt(i);
          hash = ((hash << 5) - hash) + char;
          hash = hash & hash; // Convert to 32bit integer
        }
        tableState.gameNumber.set(Math.abs(hash));
      }
    }
  }

  /** Parse a 2-character card code into a Card object */
  private parseCardCode(code: string): Card | null {
    if (!code || code.length !== 2) return null;
    const rank = code[0] as Card['rank'];
    const suit = code[1] as Card['suit'];
    return { rank, suit };
  }

  private handleSitOk(message: Record<string, unknown>): void {
    const seat = Number(message.seat ?? 0);
    const chips = Number(message.chips ?? 0);
    this.handleSitSuccess(seat, chips);
  }

  private handleSitError(message: Record<string, unknown>): void {
    const errorMsg = String(message.error ?? 'Failed to sit');
    this.emit('error', { message: errorMsg });
    this.pendingSeat = null;
  }

  /**
   * Clamp buy-in amount to server's min/max range.
   * Prevents sending invalid amounts that the server would reject.
   */
  private clampBuyIn(amount: number): number {
    const stakes = tableState.stakes.get();
    const gameInfo = tableState.gameInfo.get();
    const minBuyIn = (gameInfo?.minBuyin ?? 20) * stakes.bigBlind;
    const maxBuyIn = (gameInfo?.maxBuyin ?? 200) * stakes.bigBlind;
    const clamped = Math.max(minBuyIn, Math.min(maxBuyIn, amount));
    if (clamped !== amount) {
      log.table.warning`Buy-in ${amount} clamped to ${clamped} (range: ${minBuyIn}-${maxBuyIn})`;
    }
    return clamped;
  }

  /**
   * Handle ping from server - respond with pong to stay connected
   * Server sends: {"a": "ping"}
   * Client must respond: # pong
   */
  private handlePing(from: string): void {
    const command = Commands.pong();
    log.ping.debug`Responding to ping from ${from}`;
    networkService.send(from, command);
  }

  /**
   * Handle seat reservation confirmation from server
   * Server sends: {"a": "reserved", "sn": <seat_number>}
   */
  private handleReserved(message: Record<string, unknown>): void {
    const seat = Number(message.sn ?? 0);
    log.table.debug`Seat reserved: ${seat}`;
    // Seat is reserved, waiting for buyin confirmation
  }

  /**
   * Handle successful sit-in from server
   * Server sends: {"a": "sitin", "player": {...}, "sn": <seat>, "br": <bankroll>}
   * Note: Server uses 1-based seats, client uses 0-based
   */
  private handleSitIn(message: Record<string, unknown>): void {
    const serverSeat = Number(message.sn ?? 1);
    const seat = serverSeat - 1; // Convert to 0-based
    const bankroll = Number(message.br ?? 0);
    const playerData = message.player as Record<string, unknown> | undefined;
    const playerId = String(playerData?.id ?? '');

    log.table.info`Player sat in: ${playerId} seat ${seat} bankroll ${bankroll}`;

    // Check if this is us (hero)
    // Server sends "you" as the player id for the current player
    const myGlid = networkService.getMyGlid();
    if (playerId === myGlid || playerId === 'you') {
      // This is us sitting
      this.handleSitSuccess(seat, bankroll);
    } else {
      // Another player sat down - update table state
      const playerProfile: PlayerProfile = {
        id: playerId,
        name: String(playerData?.name ?? playerId),
      };

      // Decode CRM profile if present
      const decodedProfile = decodeProfile(playerData?.profile);
      if (decodedProfile) {
        playerProfile.crmData = decodedProfile;
      }

      const playerState: PlayerState = {
        profile: playerProfile,
        seat,
        stack: bankroll,
        bet: 0,
        status: 'active',
        position: '',
        holeCards: null,
        hasCards: false,
        isHero: false,
      };

      updatePlayer(seat, playerState);

      // P1.3.12: Emit player:joined to GameEventBus for Effects Engine
      gameEventBus.emit('player:joined', {
        seat,
        profile: playerProfile,
        stack: bankroll,
      });
    }
  }

  /**
   * Handle player left/stood up
   * Server sends: {"a": "leave" | "standup", "player": {"id": "..."}, "reason": "..."}
   * Note: These messages do NOT include seat number - must identify by player ID
   * Per POKERTALK.md:
   * - "standup" with reason "timeout" = sitout timer expired, player becomes watcher
   * - "standup" with reason "by player" = player chose to stand up
   * - "leave" = player left the table completely
   */
  private handlePlayerLeft(message: Record<string, unknown>): void {
    const playerData = message.player as Record<string, unknown> | undefined;
    const playerId = String(playerData?.id ?? '');
    const reason = message.reason as string | undefined;

    log.table.info`Player left/standup: ${playerId} reason: ${reason ?? 'unknown'}`;

    // Check if this is hero
    const myGlid = networkService.getMyGlid();
    const isHero = playerId === myGlid || playerId === 'you';

    // Map reason string to PlayerLeftEvent reason
    const reasonMap: Record<string, 'left' | 'timeout' | 'disconnected' | 'kicked'> = {
      'by player': 'left',
      'timeout': 'timeout',
      'disconnected': 'disconnected',
      'kicked': 'kicked',
    };
    const leaveReason = reasonMap[reason ?? ''] ?? 'left';

    if (isHero) {
      // Hero lost seat - remove from players map and reset hero state
      const heroSeat = tableState.heroSeat.get();
      if (heroSeat !== null) {
        // Clear sitout timer for hero
        clearSitoutTimer(heroSeat);
        this.checkStopSitoutTick();

        const players = tableState.players.get();
        const newPlayers = new Map(players);
        newPlayers.delete(heroSeat);
        setPlayers(newPlayers);

        // P1.3.13: Emit player:left to GameEventBus for Effects Engine
        gameEventBus.emit('player:left', {
          seat: heroSeat,
          playerId,
          reason: leaveReason,
        });
      }
      // Reset hero state (seat, cards, actions) and become watcher
      standUp();
      this.emit('stoodUp', undefined);
    } else {
      // Find and remove the other player by their ID
      const players = tableState.players.get();
      let foundSeat: number | null = null;

      for (const [seat, player] of players) {
        if (player.profile.id === playerId) {
          foundSeat = seat;
          break;
        }
      }

      if (foundSeat !== null) {
        // Clear sitout timer for this player
        clearSitoutTimer(foundSeat);
        this.checkStopSitoutTick();

        const newPlayers = new Map(players);
        newPlayers.delete(foundSeat);
        setPlayers(newPlayers);
        log.table.debug`Removed player from seat ${foundSeat}`;

        // P1.3.13: Emit player:left to GameEventBus for Effects Engine
        gameEventBus.emit('player:left', {
          seat: foundSeat,
          playerId,
          reason: leaveReason,
        });
      }
    }
  }

  /**
   * Handle seat free message from server
   * Server sends: {"a": "free", "sn": 1} when a player leaves and the seat becomes available
   */
  private handleSeatFree(message: Record<string, unknown>): void {
    const serverSeat = Number(message.sn ?? 0);
    const seat = serverSeat - 1; // Convert to 0-based

    log.table.debug`Seat became free: ${seat}`;

    // Check if this is hero's seat
    const heroSeat = tableState.heroSeat.get();
    if (seat === heroSeat) {
      // Hero lost seat
      clearSitoutTimer(seat);
      this.checkStopSitoutTick();
      standUp();
      this.emit('stoodUp', undefined);
    }

    // Remove player from the seat
    const players = tableState.players.get();
    if (players.has(seat)) {
      clearSitoutTimer(seat);
      this.checkStopSitoutTick();

      const newPlayers = new Map(players);
      newPlayers.delete(seat);
      setPlayers(newPlayers);
      log.table.debug`Removed player from seat ${seat}`;
    }
  }

  /**
   * Handle sitout response from server
   * Server sends: {"a": "sitout", "player": {"id": "..."}, "sitout_timer": 120}
   */
  private handleSitout(message: Record<string, unknown>): void {
    const playerData = message.player as Record<string, unknown> | undefined;
    const playerId = String(playerData?.id ?? '');
    const sitoutTimer = message.sitout_timer as number | undefined;
    const myGlid = networkService.getMyGlid();
    const isHero = playerId === myGlid || playerId === 'you';

    log.table.info`Player sitout: ${playerId} isHero: ${isHero} timer: ${sitoutTimer}`;

    // Find the player's seat and set sitout timer
    const seat = this.findPlayerSeat(playerId, isHero);
    if (seat !== null && sitoutTimer !== undefined && sitoutTimer > 0) {
      setSitoutTimer(seat, sitoutTimer);
      this.startSitoutTick();
    }

    // Update player status in players map to show sitout indicator
    this.updatePlayerStatus(playerId, isHero, 'sitout');

    // P1.3.14: Emit player:sitout to GameEventBus for Effects Engine
    if (seat !== null) {
      const sitoutEvent: Parameters<typeof gameEventBus.emit<'player:sitout'>>[1] = {
        seat,
        playerId,
      };
      if (sitoutTimer !== undefined && sitoutTimer > 0) {
        sitoutEvent.timer = sitoutTimer;
      }
      gameEventBus.emit('player:sitout', sitoutEvent);
    }

    if (isHero) {
      setHeroSitout(true);
      this.emit('satOut', undefined);
    }
  }

  /**
   * Handle back response from server
   * Server sends: {"a": "back", "player": {"id": "..."}}
   */
  private handleBack(message: Record<string, unknown>): void {
    const playerData = message.player as Record<string, unknown> | undefined;
    const playerId = String(playerData?.id ?? '');
    const myGlid = networkService.getMyGlid();
    const isHero = playerId === myGlid || playerId === 'you';

    log.table.info`Player back: ${playerId} isHero: ${isHero}`;

    // Clear sitout timer for this player
    const seat = this.findPlayerSeat(playerId, isHero);
    if (seat !== null) {
      clearSitoutTimer(seat);
      this.checkStopSitoutTick();
    }

    // Update player status in players map to remove sitout indicator
    this.updatePlayerStatus(playerId, isHero, 'active');

    // P1.3.14: Emit player:back to GameEventBus for Effects Engine
    if (seat !== null) {
      gameEventBus.emit('player:back', {
        seat,
        playerId,
      });
    }

    if (isHero) {
      setHeroSitout(false);
      this.emit('cameBack', undefined);
    }
  }

  /**
   * Handle bankroll update broadcast from server
   * Server sends: {"a":"br", "player":{"id":"player3"}, "br":400, "added":200}
   * Broadcast to ALL players when someone rebuys, refills, or addons.
   */
  private handleBankrollUpdate(message: Record<string, unknown>): void {
    const playerData = message.player as Record<string, unknown> | undefined;
    const playerId = String(playerData?.id ?? '');
    const newBankroll = Number(message.br ?? 0);
    const added = Number(message.added ?? 0);
    const myGlid = networkService.getMyGlid();
    const isHero = playerId === myGlid || playerId === 'you';

    log.table.info`Bankroll update: player=${playerId} br=${newBankroll} added=${added}`;

    const seat = this.findPlayerSeat(playerId, isHero);
    if (seat === null) {
      log.table.warning`Bankroll update for unknown player: ${playerId}`;
      return;
    }

    const players = tableState.players.get();
    const player = players.get(seat);
    if (!player) return;

    updatePlayer(seat, { ...player, stack: newBankroll });
    markBrUpdated(seat);

    log.table.debug`Updated seat ${String(seat)} stack: ${String(player.stack)} → ${String(newBankroll)} (+${String(added)})`;
  }

  /**
   * Handle rebuy request from server
   * Server sends: {"a": "rebuy", "at": "end"|"start", "info": {...}}
   * Per POKERTALK.md - player must respond with # rebuy command within poor_timeout
   */
  private handleRebuy(message: Record<string, unknown>): void {
    const at = (message.at as 'end' | 'start') ?? 'end';
    const info = message.info as Record<string, unknown> | undefined;

    // Extract buy-in limits and timeout from info (min/max are BB multipliers)
    const bigBlind = Number(info?.bb ?? tableState.stakes.get().bigBlind);
    const minBuyIn = Number(info?.min_buyin ?? 20) * bigBlind;
    const maxBuyIn = Number(info?.max_buyin ?? 200) * bigBlind;
    const timeout = Number(info?.poor_timeout ?? 60);

    log.table.info`Rebuy requested: at=${at} min=${minBuyIn} max=${maxBuyIn} timeout=${timeout}s`;

    // Emit event to show rebuy popup
    this.emit('rebuyRequested', {
      minBuyIn,
      maxBuyIn,
      timeout,
      at,
    });
  }

  /**
   * Send rebuy command to server
   * Command: # rebuy [AMOUNT] [AUTOREFILL] [AUTOREBUY]
   * @param amount - Rebuy amount
   * @param autoRefill - Enable auto refill (1 = on, 0 = off)
   * @param autoRebuy - Enable auto rebuy (1 = on, 0 = off)
   */
  sendRebuy(amount: number, autoRefill: boolean, autoRebuy: boolean): void {
    if (!networkService.isConnected() || !this.currentCroupier) {
      this.emit('error', { message: 'Not connected to table' });
      return;
    }

    const autoRefillFlag = autoRefill ? 1 : 0;
    const autoRebuyFlag = autoRebuy ? 1 : 0;
    const command = `# rebuy ${amount} ${autoRefillFlag} ${autoRebuyFlag}`;

    log.table.info`Sending rebuy: amount=${amount} autoRefill=${autoRefill} autoRebuy=${autoRebuy}`;
    networkService.send(this.currentCroupier, command);
  }

  /**
   * Update a player's status in the players map
   * Used by sitout/back handlers to update visual state
   */
  private updatePlayerStatus(playerId: string, isHero: boolean, status: PlayerState['status']): void {
    const players = tableState.players.get();

    // Find player by ID (hero uses 'hero' as ID in local state)
    for (const [seat, player] of players) {
      const isMatch = isHero ? player.isHero : player.profile.id === playerId;
      if (isMatch) {
        const updatedPlayer: PlayerState = {
          ...player,
          status,
        };
        updatePlayer(seat, updatedPlayer);
        break;
      }
    }
  }

  /**
   * Find a player's seat by their ID
   */
  private findPlayerSeat(playerId: string, isHero: boolean): number | null {
    if (isHero) {
      return tableState.heroSeat.get();
    }

    const players = tableState.players.get();
    for (const [seat, player] of players) {
      if (player.profile.id === playerId) {
        return seat;
      }
    }
    return null;
  }

  /**
   * Start the sitout countdown tick interval
   */
  private startSitoutTick(): void {
    if (this.sitoutTickInterval !== null) return;

    this.sitoutTickInterval = window.setInterval(() => {
      tickSitoutTimers();
      this.checkStopSitoutTick();
    }, 1000);

    log.table.debug`Started sitout tick interval`;
  }

  /**
   * Stop the sitout countdown tick interval
   */
  private stopSitoutTick(): void {
    if (this.sitoutTickInterval !== null) {
      clearInterval(this.sitoutTickInterval);
      this.sitoutTickInterval = null;
      log.table.debug`Stopped sitout tick interval`;
    }
  }

  /**
   * Check if we should stop the sitout tick (no more timers active)
   */
  private checkStopSitoutTick(): void {
    const timers = tableState.sitoutTimers.get();
    if (timers.size === 0) {
      this.stopSitoutTick();
    }
  }

  /**
   * Handle error responses from server
   *
   * Server error format:
   * {
   *   "a": "error",
   *   "code": 403,
   *   "reason": "Not enough chips to buyin",
   *   "info": { ... }
   * }
   *
   * Buyin-related error codes:
   * - 403: Not enough chips to buyin
   * - 404: Player does not exist
   * - 405: Not enough chips to rebuy
   * - 433: Wrong request
   * - 444: No free seat
   */
  private handleServerError(message: Record<string, unknown>): void {
    // Server sends 'reason' field, fallback to 'msg' or 'error' for legacy
    const errorMsg = String(message.reason ?? message.msg ?? message.error ?? 'Server error');
    const errorCode = typeof message.code === 'number' ? message.code : undefined;

    log.table.error`Server error (code ${errorCode}): ${errorMsg}`;

    // 444 (no free seat) on a non-specific seat request → let user pick another seat
    if (errorCode === 444 && this.pendingSeat === null) {
      log.table.warning`No free seat — user can try picking a specific seat`;
      this.emit('error', { message: 'No free seats available. Try clicking a specific seat.' });
      return;
    }

    // Buyin-related error codes trigger the buyinError event
    const buyinErrorCodes = [403, 404, 405, 433, 444];
    if (errorCode !== undefined && buyinErrorCodes.includes(errorCode)) {
      this.emit('buyinError', { message: errorMsg, code: errorCode });
    } else {
      this.emit('error', { message: errorMsg });
    }

    this.pendingSeat = null;
  }

  private handleSitSuccess(seat: number, buyIn: number): void {
    // Create hero player state
    const heroProfile: PlayerProfile = {
      id: 'hero',
      name: 'You',
    };

    const heroState: PlayerState = {
      profile: heroProfile,
      seat,
      stack: buyIn,
      bet: 0,
      status: 'active',
      position: '',
      holeCards: null,
      hasCards: false,
      isHero: true,
    };

    // Update table state
    sitDown(seat);
    updatePlayer(seat, heroState);

    this.emit('seated', { seat, buyIn });
  }

}

/** Singleton table controller instance */
export const tableController = TableController.getInstance();
